import { useRef, useState } from "react";
import assistantService from "../../services/assistantService";
import { validateFiles } from "../../helpers/fileValidation";
import FileWithRag from "../../services/FileWithRag";
import { extractUserFromToken } from "../../helpers/auth";
import fileService from "../../services/fileService";
import { useDashboardUI } from "../../context/GlobalContext";

export default function UploadAssistantFileModal({
  open,
  onClose,
  assistantId,
  assistantFiles,
  onSuccess,
}) {
  const { setPageLoader } = useDashboardUI();
  const currentUser = extractUserFromToken();
  const userId = currentUser?.user_id;

  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  if (!open) return null;

  const handleFiles = (selectedFiles) => {
    const fileArray = Array.from(selectedFiles);

    const validationError = validateFiles(
      fileArray,
      files.map((f) => f.file)
    );

    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);

    const formatted = fileArray.map((file) => ({
      file,
      id: crypto.randomUUID(),
    }));

    setFiles((prev) => [...prev, ...formatted]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleUpload = async () => {
    if (!files.length) return;

    try {
      setIsUploading(true);
      setPageLoader(true);

      const formData = new FormData();

      // Append multiple files
      files.forEach((item) => {
        formData.append("file", item.file);
      });

      // Metadata
      formData.append("user_id", userId);
      // formData.append("workspace_id", "");
      formData.append("temporary", "false");
      formData.append("chat_tableupdate", "false");
      formData.append("assistant_id", assistantId);

      // Step 1: Upload to Azure
      const uploadResponse = await FileWithRag.uploadFileInBlob(formData);

      if (!uploadResponse || !uploadResponse.length) {
        throw new Error("Upload response empty");
      }

      //Step 2: Transform response (remove web_url, add assistant_id)
      const dbPayload = uploadResponse.map((file) => {
        const { web_url, message, collection_id, ...rest } = file;

        return {
          ...rest,
          assistant_id: assistantId,
          workspace_id: file.workspace_id || null,
        };
      });


      //Step 3: Save metadata to PostgreSQL
      const uploadedFiles = await fileService.createFiles(dbPayload);
    
      const currentFileIds = assistantFiles.map((f) => f.file_id);
      const fileIds = uploadedFiles.map((file) => file.id);
      await assistantService.updateAssistant(assistantId, { "file_ids": [...currentFileIds, ...fileIds] });



      //Step 4: Only after DB save success
      onSuccess?.();
      onClose();
      setFiles([]);

    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setIsUploading(false);
      setPageLoader(false);
    }
  };




  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">

      <div className="w-[700px] max-w-[calc(100vw-1.5rem)] max-h-[80vh] bg-white rounded-2xl shadow-xl flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">
            Upload Assistant Files
          </h2>

          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 overflow-auto">

          {/* Drag Drop */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current.click()}
            className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 sm:p-10 text-center cursor-pointer transition
            ${dragActive
                ? "border-blue-500 bg-blue-50"
                : "border-gray-300 bg-gray-50"
              }`}
          >
            <p className="text-sm text-gray-600">
              Drag & drop files here or click to browse
            </p>

            <input
              type="file"
              multiple
              ref={fileInputRef}
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
          </div>

          {error && (
            <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
              {error}
            </div>
          )}


          {/* File List */}
          {files.length > 0 && (
            <div className="mt-6 space-y-3">
              {files.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between gap-3 bg-gray-50 rounded-lg px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium break-words">
                      {item.file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(item.file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>

                  <button
                    onClick={() => removeFile(item.id)}
                    className="shrink-0 text-red-600 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-4 sm:px-6 py-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border text-sm"
          >
            Cancel
          </button>

          <button
            onClick={handleUpload}
            disabled={!files.length || isUploading}
            className="px-6 py-2 rounded-lg bg-blue-500 text-white text-sm disabled:opacity-50"
          >
            {isUploading ? "Uploading..." : "Upload"}
          </button>
        </div>

      </div>
    </div>
  );
}
